import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { styled } from '@linaria/react';
import { showOverlay } from 'modules/overlay/slice';
import { OverlayType } from 'modules/overlay/types';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { ContentWrapper } from '@daedalus/atlas/helpers/ContentWrapper';
import { Text } from '@daedalus/atlas/Text';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { formatPriceValue } from '@daedalus/core/src/price/business/formatPrice';
import { useGetHotelQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { ContentHotel } from '@findhotel/sapi/dist/types/packages/core/src/types';
import { ButtonLinkLoader } from './ButtonLink';
import { CheckInCheckOut } from './CheckInCheckOut';
import { PoliciesDetailsOverlay } from './PoliciesDetailsOverlay';
import { PolicySubSection } from './types';
import { getTitle, isSubSectionAvailable, policySubSections } from './utils';
const PolicesWrapper = /*#__PURE__*/styled('div')({
  name: "PolicesWrapper",
  class: "pd778nn",
  propsAsIs: false
});
interface Props {
  hotelId: string | undefined;
}
const SubSectionButton = ({
  hotel,
  section
}: {
  hotel: undefined | ContentHotel;
  section: PolicySubSection;
}) => {
  const {
    formatMessage,
    locale
  } = useIntl();
  const dispatch = useDispatch();
  const dispatchTeamEvent = useDispatchTeamEvent();
  const showSubSection = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsPropertyPolicy,
      action: Action.Clicked,
      payload: {
        selectedElement: section
      }
    }));
    dispatch(showOverlay({
      type: OverlayType.HotelPolicies,
      params: {
        policySubSection: section
      }
    }));
  };
  if (!isSubSectionAvailable(hotel, section)) return null;
  const showDepositAmount = section === PolicySubSection.SecurityDeposit && Boolean(hotel.deposit?.value);
  return <ButtonLink onClick={showSubSection} size="md" isCompact variant="default">
      {formatMessage(getTitle(section))}
      {showDepositAmount && ` · ${formatPriceValue({
      locale,
      value: hotel.deposit.value.amount,
      currency: hotel.deposit.value.currency
    })}`}
    </ButtonLink>;
};
export const Policies = ({
  hotelId
}: Props) => {
  const dispatchTeamEvent = useDispatchTeamEvent();
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });

  /**
   * Fetch the full ContentHotel which includes translated policies data
   */
  const {
    data: contentHotel,
    isUninitialized,
    isLoading
  } = useGetHotelQuery({
    hotelId
  }, {
    skip: !hotelId
  });
  useEffect(() => {
    if (inView) dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsPropertyPolicySection,
      action: Action.Viewed
    }));
  }, [dispatchTeamEvent, inView]);
  return <>
      <div ref={ref}>
        <Text variant="titleL">
          <FormattedMessageWrapper id="propertyPolicy" defaultMessage="Property policies" />
        </Text>

        <ContentWrapper paddingTop="s500">
          <CheckInCheckOut hotel={contentHotel} isLoading={isUninitialized || isLoading || !contentHotel} layout="primary" />
        </ContentWrapper>

        <ContentWrapper paddingTop="s500">
          <PolicesWrapper>
            {Object.keys(policySubSections).map((section: PolicySubSection) =>
          // Button breaks stack margins
          <div key={section} data-id={section}>
                {isUninitialized || isLoading ? <ButtonLinkLoader /> : <SubSectionButton hotel={contentHotel} section={section} />}
              </div>)}
          </PolicesWrapper>
        </ContentWrapper>
      </div>
      <PoliciesDetailsOverlay hotelId={hotelId} />
    </>;
};

require("./Policies.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Policies.tsx");